import { lazy, Suspense } from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import { AppShell } from "@/components/app-shell"
import { useAuth } from "@/hooks/use-auth"

const DashboardPage = lazy(() => import("@/pages/dashboard-page").then((module) => ({ default: module.DashboardPage })))
const LoginPage = lazy(() => import("@/pages/login-page").then((module) => ({ default: module.LoginPage })))
const CatalogPage = lazy(() => import("@/pages/catalog-page").then((module) => ({ default: module.CatalogPage })))
const CatalogProductPage = lazy(() => import("@/pages/catalog-product-page").then((module) => ({ default: module.CatalogProductPage })))
const CatalogItemPage = lazy(() => import("@/pages/catalog-item-page").then((module) => ({ default: module.CatalogItemPage })))
const CalendarPage = lazy(() => import("@/pages/calendar-page").then((module) => ({ default: module.CalendarPage })))
const MyTasksPage = lazy(() => import("@/pages/my-tasks-page").then((module) => ({ default: module.MyTasksPage })))
const NotFoundPage = lazy(() => import("@/pages/not-found-page").then((module) => ({ default: module.NotFoundPage })))
const NotificationsPage = lazy(() => import("@/pages/notifications-page").then((module) => ({ default: module.NotificationsPage })))
const ProductionPage = lazy(() => import("@/pages/production-page").then((module) => ({ default: module.ProductionPage })))
const ProductionDetailPage = lazy(() => import("@/pages/production-detail-page").then((module) => ({ default: module.ProductionDetailPage })))
const ReportsPage = lazy(() => import("@/pages/reports-page").then((module) => ({ default: module.ReportsPage })))
const PrintShopsPage = lazy(() => import("@/pages/print-shops-page").then((module) => ({ default: module.PrintShopsPage })))
const SettingsPage = lazy(() => import("@/pages/settings-page").then((module) => ({ default: module.SettingsPage })))
const ImprintPage = lazy(() => import("@/pages/imprint-page").then((module) => ({ default: module.ImprintPage })))
const SettingsImprintsPage = lazy(() => import("@/pages/settings-imprints-page").then((module) => ({ default: module.SettingsImprintsPage })))
const SettingsCollectionsPage = lazy(() => import("@/pages/settings-collections-page").then((module) => ({ default: module.SettingsCollectionsPage })))
const SettingsGoogleDrivePage = lazy(() => import("@/pages/settings-google-drive-page").then((module) => ({ default: module.SettingsGoogleDrivePage })))
const SettingsItemTypesPage = lazy(() => import("@/pages/settings-item-types-page").then((module) => ({ default: module.SettingsItemTypesPage })))
const SettingsKitTemplatesPage = lazy(() => import("@/pages/settings-kit-templates-page").then((module) => ({ default: module.SettingsKitTemplatesPage })))
const SettingsPipelinesPage = lazy(() => import("@/pages/settings-pipelines-page").then((module) => ({ default: module.SettingsPipelinesPage })))
const SettingsStageEstimatesPage = lazy(() => import("@/pages/settings-stage-estimates-page").then((module) => ({ default: module.SettingsStageEstimatesPage })))
const SettingsUsersPage = lazy(() => import("@/pages/settings-users-page").then((module) => ({ default: module.SettingsUsersPage })))

function PageFallback() {
  return <div className="grid min-h-72 place-items-center"><div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
}

function ProtectedLayout() {
  const { user, loading } = useAuth()
  if (loading) return <div className="grid min-h-screen place-items-center bg-background"><div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
  if (!user) return <Navigate to="/login" replace />
  return <AppShell />
}

function AdminRoute() {
  const { user } = useAuth()
  if (user?.role !== "ADMIN") return <Navigate to="/" replace />
  return <Outlet />
}

export function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="minhas-tarefas" element={<MyTasksPage />} />
          <Route path="catalogo" element={<CatalogPage />} />
          <Route path="catalogo/:productId" element={<CatalogProductPage />} />
          <Route path="catalogo/:productId/itens/:itemId" element={<CatalogItemPage />} />
          <Route path="producao" element={<ProductionPage />} />
          <Route path="producao/:runId" element={<ProductionDetailPage />} />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="graficas" element={<PrintShopsPage />} />
          <Route path="relatorios" element={<ReportsPage />} />
          <Route path="notificacoes" element={<NotificationsPage />} />
          <Route element={<AdminRoute />}>
            <Route path="selos" element={<SettingsImprintsPage />} />
            <Route path="selos/novo" element={<ImprintPage creating />} />
            <Route path="selos/:imprintId" element={<ImprintPage />} />
            <Route path="configuracoes" element={<Outlet />}>
              <Route index element={<SettingsPage />} />
              <Route path="selos" element={<Navigate to="/selos" replace />} />
              <Route path="colecoes" element={<SettingsCollectionsPage />} />
              <Route path="itens-dos-kits" element={<SettingsItemTypesPage />} />
              <Route path="modelos-de-kits" element={<SettingsKitTemplatesPage />} />
              <Route path="fluxos-de-trabalho" element={<SettingsPipelinesPage />} />
              <Route path="pipelines" element={<Navigate to="/configuracoes/fluxos-de-trabalho" replace />} />
              <Route path="estimativas" element={<SettingsStageEstimatesPage />} />
              <Route path="google-drive" element={<SettingsGoogleDrivePage />} />
              <Route path="usuarios" element={<SettingsUsersPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
